// A play before it is sent: the SDK's ticket (stake, bust, refusal, payout at each move, all in
// exact integers), the move that would reach the cash-out target, and the odds the floor shows.
// The odds are estimates from Hyperliquid's own volatility; no amount is ever a float.

import { useMemo } from "react";
import { pt, type Instrument, type TicketPreview } from "../sdk";
import { big } from "../lib/num";
import { expectedMinutes, minutesToChance, targetFirst, touchWithin } from "../odds/model";
import type { Call, Clock, Target } from "./slip";

export type Odds = {
  /** Chance the target pays before the bust. Null when riding with no target. */
  target: number | null;
  /** What a fair game would give the same target: 1 / multiple. */
  fair: number | null;
  /** Return to player at the target: chance × multiple. */
  rtp: number | null;
  /** Expected minutes until the play settles at one line or the other (target or bust). */
  settles: number | null;
  /** Chance of the bust inside the clock, if one is set. */
  bustInClock: number | null;
  /** Minutes until the bust alone is a coin flip. */
  halfLife: number | null;
};

export type Quote = {
  preview: TicketPreview;
  /** The move that reaches the target, in basis points of the mark (positive), or null. */
  targetBp: number | null;
  /** The mark at which the target pays, raw at the market's scale. */
  targetMark: string | null;
  /** The payout at the target (stake plus winnings), raw collateral. */
  targetPayout: bigint | null;
  /** The bust's distance from the mark, as a fraction. */
  bustDistance: number | null;
  odds: Odds;
};

type Input = {
  owner: string | null;
  instrument: Instrument;
  call: Call;
  stake: string;
  leverage: number;
  target: Target;
  clock: Clock;
  mark: string | null;
  sigma: number | null;
};

/** Payout pnl needed for `multiple` × stake, in tenths so 1.5 stays exact. */
const needed = (margin: bigint, multiple: number) => (margin * BigInt(Math.round(multiple * 10) - 10)) / 10n;

function ticket(input: Input, movesBp: number[]): TicketPreview {
  return pt.ticket({
    owner: input.owner,
    instrumentId: input.instrument.id,
    isLong: input.call === "up",
    margin: input.stake,
    leverage: input.leverage,
    useDebt: false,
    mark: input.mark,
    movesBp,
  });
}

/** The smallest move (bp, positive) at which the payout reaches `goal`, searched coarse then fine. */
function moveFor(input: Input, goal: bigint): { bp: number; mark: string } | null {
  const sign = input.call === "up" ? 1 : -1;
  const base = Math.max(1, Math.ceil(10_000 / input.leverage));
  const step = Math.max(1, Math.floor(base / 4));
  const coarse: number[] = [];
  for (let bp = step; bp <= 9_000 && coarse.length < 160; bp += step) coarse.push(bp);
  const first = ticket(input, coarse.map((bp) => bp * sign)).scenarios ?? [];
  const hit = first.find((s) => !s.liquidated && big(s.payoutPnl) >= goal);
  if (!hit) return null;
  const hi = Math.abs(hit.moveBp);
  const lo = Math.max(1, hi - step + 1);
  if (lo === hi) return { bp: hi, mark: hit.mark };
  const fine: number[] = [];
  for (let bp = lo; bp <= hi; bp += 1) fine.push(bp * sign);
  const second = ticket(input, fine).scenarios ?? [];
  const exact = second.find((s) => !s.liquidated && big(s.payoutPnl) >= goal) ?? hit;
  return { bp: Math.abs(exact.moveBp), mark: exact.mark };
}

export function quote(input: Input): Quote {
  const preview = ticket(input, []);
  const margin = big(preview.margin);
  const bustDistance = preview.bustPpm ? Number(preview.bustPpm) / 1_000_000 : null;
  let targetBp: number | null = null;
  let targetMark: string | null = null;
  let targetPayout: bigint | null = null;
  if (input.target !== null && margin > 0n && input.mark) {
    const goal = needed(margin, input.target);
    const found = moveFor(input, goal);
    if (found) {
      targetBp = found.bp;
      targetMark = found.mark;
      targetPayout = margin + goal;
    }
  }

  const odds: Odds = { target: null, fair: null, rtp: null, settles: null, bustInClock: null, halfLife: null };
  if (bustDistance !== null && bustDistance > 0) {
    if (targetBp !== null && input.target !== null) {
      const up = targetBp / 10_000;
      odds.target = targetFirst(up, bustDistance);
      odds.fair = 1 / input.target;
      odds.rtp = odds.target * input.target;
      if (input.sigma) odds.settles = expectedMinutes(up, bustDistance, input.sigma);
    }
    if (input.sigma) {
      odds.halfLife = minutesToChance(bustDistance, input.sigma, 0.5);
      if (input.clock) odds.bustInClock = touchWithin(bustDistance, input.sigma, input.clock / 60_000);
    }
  }
  return { preview, targetBp, targetMark, targetPayout, bustDistance, odds };
}

/** The quote for the slip as it stands, recomputed when any input moves. */
export function useQuote(input: Input | null, revision: unknown): Quote | null {
  return useMemo(
    () => (input ? quote(input) : null),
    // the SDK reads the account it holds; `revision` says when that moved
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      input?.owner,
      input?.instrument,
      input?.call,
      input?.stake,
      input?.leverage,
      input?.target,
      input?.clock,
      input?.mark,
      input?.sigma,
      revision,
    ],
  );
}

/** Live odds for an open play: the target and bust as distances from the mark now. */
export function liveOdds(isLong: boolean, mark: number, bust: number, targetMark: number | null, sigma: number | null) {
  const none = { target: null as number | null, settles: null as number | null };
  if (!(mark > 0) || !(bust > 0) || targetMark === null || !(targetMark > 0)) return none;
  const sign = isLong ? 1 : -1;
  const down = (sign * (mark - bust)) / mark;
  const up = (sign * (targetMark - mark)) / mark;
  if (down <= 0) return { target: 0, settles: 0 };
  if (up <= 0) return { target: 1, settles: 0 };
  return { target: targetFirst(up, down), settles: sigma ? expectedMinutes(up, down, sigma) : null };
}
